import { get } from 'lodash';
import React, { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import Typography from '@material-ui/core/Typography';
import {
  Person,
  PhysicalPerson,
} from '../../../../../../global/entities/Person';
import { RHFInputText } from '../../../../../../packages/react-hook-form';
import { MaskedSwedishSsnInput } from '../../../../../input/components';
import { MaskedSwedishPostNumberInput } from '../../../../../input/components/masked-swedish-postnumber-input';
import { FieldArrayInputProps } from '../physical-person-input';
import {
  createFieldArrayFieldSelector,
  createFieldArrayInputName,
} from '../../../../../../utils/field-array/create-field-array-input-name';
import {
  ObjectPathFn,
  TypedObjectAccessor,
} from '../../../../../../utils/types';
import { emptyPersonObject } from '../../../representatives/representatives-utils';
import { useSelectPeople } from '../../../../use-buy-process-form';
import { useBuyProcessForm } from '../../../../buy-process-form-utils';
import { BuyOrderRelation } from '../../../../../../utils/types/buy-order-relations';
import { RHFAutocompleteMultiField } from '../../../../../../packages/react-hook-form/autocomplete-multi-field/rhf-autocomplete-multifield';
import { Stack } from '../../../../../stack/stack';
import { SWEDISH_SSN_PLACEHOLDER } from '../../../../../../global/constants';

interface PersonFieldInputProps
  extends Omit<FieldArrayInputProps<Person>, 'remove'> {
  /** Optionally provide an object containing values to be set when autocompleting */
  extraFieldValues?: Record<string, unknown>;
  margin?: 'dense' | 'normal';
}

export const PersonFieldInput = ({
  index,
  field,
  extraFieldValues,
  fieldArrayName,
  margin = 'dense',
}: PersonFieldInputProps) => {
  const createName: ObjectPathFn<BuyOrderRelation<PhysicalPerson>> = (
    name: string,
  ) => createFieldArrayInputName(fieldArrayName, index, name);

  const getFieldProperty: TypedObjectAccessor<PhysicalPerson> = (
    name: string,
  ) => get(field, name);

  const fieldName = createFieldArrayFieldSelector(fieldArrayName, index);

  const { control, errors, setValue, getValues } = useFormContext();
  const { id } = useBuyProcessForm();
  const people = useSelectPeople(id);

  const getDefaultValue = () => {
    const field = get(getValues(), fieldName);

    if (field) {
      return people.find(
        (x) => x.socialSecurityNumber === field.socialSecurityNumber,
      );
    }

    return null;
  };

  const handleAutocompleteField = (person: Person) => {
    setValue(
      fieldName,
      { ...person, ...extraFieldValues },
      {
        shouldValidate: true,
        shouldDirty: true,
      },
    );
  };

  const handleAutocompletePrimitive = (value: string) => {
    setValue(createName('firstName'), value, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const renderAutocompleteOption = (option: Person) => (
    <Typography variant="caption">
      {option.firstName} {option.lastName} ({option.socialSecurityNumber})
    </Typography>
  );

  // Run once to populate the field
  useEffect(() => {
    setValue(fieldName, getDefaultValue());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // These fields here must be controlled, otherwise MaterialUI's label will
  // never move when their values are set from the outside using `setValue`.
  // https://github.com/react-hook-form/react-hook-form/issues/2192

  return (
    <React.Fragment>
      <Stack direction="row" gap={8}>
        <RHFAutocompleteMultiField
          handleAutocompleteField={handleAutocompleteField}
          handleAutocompletePrimitive={handleAutocompletePrimitive}
          renderOption={renderAutocompleteOption}
          handleClearFields={() => setValue(fieldName, emptyPersonObject)}
          options={people}
          name={createName('firstName')}
          margin="dense"
          required
          label="Samtliga förnamn"
          control={control}
          errors={errors}
          defaultValue={getFieldProperty('firstName') as string}
          getOptionLabel={(option) => option.firstName}
          fullWidth
          freeSolo
        />

        <RHFInputText
          control={control}
          margin={margin}
          name={createName('lastName')}
          errors={errors}
          label="Efternamn"
          required
          defaultValue={getFieldProperty('lastName')}
        />
      </Stack>

      <RHFInputText
        placeholder={SWEDISH_SSN_PLACEHOLDER}
        control={control}
        margin={margin}
        name={createName('socialSecurityNumber')}
        errors={errors}
        label="Personnummer"
        InputProps={{ inputComponent: MaskedSwedishSsnInput }}
        required
        defaultValue={getFieldProperty('socialSecurityNumber')}
      />

      <RHFInputText
        control={control}
        margin={margin}
        name={createName('address')}
        errors={errors}
        label="Utdelningsadress"
        required
        defaultValue={getFieldProperty('address')}
      />

      <Stack direction="row" gap={8}>
        <RHFInputText
          control={control}
          margin={margin}
          name={createName('postalCode')}
          errors={errors}
          label="Postnummer"
          InputProps={{ inputComponent: MaskedSwedishPostNumberInput }}
          required
          defaultValue={getFieldProperty('postalCode')}
        />

        <RHFInputText
          control={control}
          margin={margin}
          name={createName('city')}
          errors={errors}
          label="Ort"
          required
          defaultValue={getFieldProperty('city')}
        />
      </Stack>
    </React.Fragment>
  );
};
